
// @ts-check

/**
 *  @typedef {import('./coreTypes').JSDonorData} JSDonorData
 *  @typedef {import('./coreTypes').ResultAtom} ResultAtom
 *  @typedef {import('./coreTypes').DonorData} DonorData
 */


/**
 * @param {ResultAtom[]} donorCardList 
 * @return {ResultAtom[]}
 */
function mergeDonorCards(donorCardList) {

    const cardsById = groupCardsById(donorCardList);

    const multiCards = Object.values(cardsById);
  
    /**
     * @type {(ResultAtom|null)[]}
     */
    let result = donorCardList;
    
    for (let iter = 0; iter < multiCards.length; iter++) {
        const mc = multiCards[iter];
        const indices = mc['indices'];
        const cards = mc['cards'];

        if (indices.length === 0) continue;
    
        const merged = mergeCardsIntoOne(cards);
        if (merged === null) continue;

        result[indices[0]] = merged;
        for (let i = 1; i < indices.length; i++) {
            result[indices[i]] = null;
        }
    }
    
    return removeBlankCards(dedupLegacyCards(result.flatMap(r => (r === null) ? [] : [r])));
}

/**
 * @param {ResultAtom[]} donorCardList 
 * @return {ResultAtom[]}
 */
function removeBlankCards(donorCardList) {
    /** 
     * @param {Object.<string, Array<string>>|null} cc
     * @return {boolean}
    */
    const campCommentEmpty = cc => {
        if (cc === null) return true;
        if (Object.keys(cc).length === 0) return true;
        if (Object.values(cc).every(v => v.length === 0)) return true;
        return false;
    };
    return donorCardList.filter(c => {
        if (c[1]['first_name'] === ''
            && c[1]['last_name'] === ''
            && campCommentEmpty(c[1]['campaign_comments'])) return false;
        return true;
    });
}

/**
 * @param {ResultAtom[]} donorCardList
 * @return {Object.<string, { indices: number[], cards: ['donor', DonorData][]}>}
 */
function groupCardsById(donorCardList) {

    /** @type {Object.<string, { indices: number[], cards: ['donor', DonorData][]}>} */
    let cardsById = {};

    for (let i = 0; i < donorCardList.length; i++) {
        const dc = donorCardList[i];
        
        if (dc[0] !== 'donor') continue;
        
        const id = dc[1]['id'];
    
        if (id in cardsById) {
            cardsById[id]['indices'].push(i);
            cardsById[id]['cards'].push(dc);
        } 
        else {
            cardsById[id] = { 'indices': [i], 'cards': [dc] };
        }
      }
  
      return cardsById;
}


/**
 * @param {['donor', DonorData][]} cards 
 * @return {(['donor', DonorData] | null)}
 */
function mergeCardsIntoOne(cards) {

    if (cards.length === 0) return null;

    /** @type {['donor', DonorData]} */
    const res = 
        ['donor', 
        { 
            'id': cards[0][1]['id'],
            'first_name': cards[0][1]['first_name'],
            'last_name': cards[0][1]['last_name'],
            'donor_since': cards[0][1]['donor_since'] ?? null,
            'alum_year': cards[0][1]['alum_year'] ?? null,
            'campaigns':
                // see https://stackoverflow.com/questions/1960473 for the filter unique trick.
                 cards.flatMap(c => c[1]['campaigns'] ?? []).filter((v, i, a) => a.indexOf(v) === i),
            'campaign_comments': 
                // TODO: Figure out if duplicate entries are properly handled.
                 cards.map(c => c[1]['campaign_comments'] ?? {}).reduce((c, i) => { return {...c, ...i}; }),
        }];

    return res;
}


/**
 * @param {ResultAtom[]} cards 
 * @return {ResultAtom[]}
 */
function dedupLegacyCards(cards) {
    return cards.reduce(
        /**
         * @param {ResultAtom[]} c 
         * @param {ResultAtom} i 
         * @returns {ResultAtom[]}
         */
        (c, i) => {
            if (i[0] === 'donor_legacy') {
                for (let iter = 0; iter < c.length; iter++) {
                    const d = c[iter];
                    if (d[0] !== 'donor_legacy') continue;
                    if (legacyCardsAreSame(d, i)) return c;
                }
            } 
            return c.concat([i]);
        }, []);
}


/**
 * @param {ResultAtom} a 
 * @param {ResultAtom} b 
 * @return {boolean}
 */
function legacyCardsAreSame(a, b) {

    /**
     * @template T
     * @param {T[]} x 
     * @param {T[]} y 
     * @return boolean
     */
    const arraySetsAreSame = (x, y) => {
        if (x.length !== y.length) return false;
        for (let i = 0; i < x.length; i++) {
            if (!y.includes(x[i])) return false;
            if (!x.includes(y[i])) return false;
        }
        return true;
    };

    /**
     * @template T
     * @param {T[]} x 
     * @param {T[]} y 
     * @return boolean
     */
    const arraysAreSequenceSame = (x, y) => {
        if (x.length !== y.length) return false;
        for (let i = 0; i < x.length; i++) {
            if (x[i] !== y[i]) return false;
        }
        return true;
    };


    const aa = a[1];
    const bb = b[1];

    if (aa['first_name'] !== bb['first_name']) return false;
    if (aa['last_name'] !== bb['last_name']) return false;

    if (aa['campaigns'] === null && bb['campaigns'] === null) return true;
    if (aa['campaigns'] === null || bb['campaigns'] === null) return false;

    if(aa['campaigns'].length !== bb['campaigns'].length) return false;

    if (!arraySetsAreSame(aa['campaigns'], bb['campaigns'])) return false;

    /** @type {Object.<string, Array<string>>|null} */
    const acc = aa['campaign_comments'];
    /** @type {Object.<string, Array<string>>|null} */
    const bcc = bb['campaign_comments'];

    if (acc === null && bcc === null) return true;
    if (acc === null || bcc === null) return false;
    
    if (acc.length !== bcc.length) return false;

    if (!arraySetsAreSame(Object.keys(acc), Object.keys(bcc))) return false;

    let bad = false;
    Object.keys(acc).forEach((v, i, a) => {
        if (!bad) {
            if (!arraysAreSequenceSame(acc[v], bcc[v])) { bad = true; }
        }
    });
    if (bad) return false;

    return true;
}


export { mergeDonorCards, mergeCardsIntoOne };

